import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { useSnackbar } from "../../../assets/vfx/snackbar/vfxSnackbarContext";
import {
  GRID_ACTION_TYPES,
  GridActionType,
  Module,
  QueryKeyTypes,
} from "../../../assets/types/common";
import { isGridActionType } from "../../../assets/utils/helpers";
import { Constants } from "../../../assets/utils/lib/constants";
import { MessageSeverity } from "../../../assets/utils/lib/messageSeverity";
import { LoadingIndicator } from "../../../assets/vfx/loadingIndicator/vfxLoadingIndicator";
import { IActionResponse, IActionResult } from "../../../services/proxies";
import { ViewProps } from "../props";
import { LookupGroupForm as EditComponent } from "./lookupGroupForm";
import { LookupGroupPage } from "./lookupGroupPage";
import { LookupGroupService } from "./lookupGroupService";
import { LookupGroupView as LookupGroupViewComponenet } from "./lookupGroupView";
import { apiErrorMessage } from "../../../assets/utils/lib/error";
import { useActionFromQuery } from "../../../hooks/useActionFromQuery";

interface LookupGroupProps<T> {
  moduleType: Module;
}

export const LookupGroupContainer = <T extends ViewProps.GroupTypeDto>(
  props: React.PropsWithoutRef<LookupGroupProps<T>>
) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = React.useState<T>();
  const [drawerType, setDrawerType] = React.useState<GridActionType>(
    GRID_ACTION_TYPES.HIDDEN
  );

  const { setSearchAction, clearSearchAction } = useActionFromQuery(OnCustomEvent);

  const service: LookupGroupService<T> = new LookupGroupService<T>(
    props.moduleType
  );

  const lookupGroupQuery = service.getLookupGroupQuery(
    data?.id ?? Constants.emptyGuid
  );

  const lookupQuery = service.getLookups();

  function EditRecord(data: any) {
    setData(data);
      if (data?.id)
          setSearchAction(GRID_ACTION_TYPES.EDIT, data.id);
  }

  function OnCustomEvent(actionType: string, dataId: string) {
      if (isGridActionType(actionType)) {
          setDrawerType(actionType);
          setData({ id: dataId } as T);
          setSearchAction(actionType, dataId);
      }
  }

  const { status, mutateAsync: mutate } = service.save();
  const { status: deactivatestatus, mutate: deactivateMutate } =
    service.deActivate();

  const handleOnSubmit = async (values: T) => {
    const isEdit = data && data.id && data.id !== Constants.emptyGuid;
    values.id = isEdit ? data.id : Constants.emptyGuid;
    return await mutate(values, {
      onSuccess: (response: IActionResult<T>) =>
        handleResponse(response, "Lookup Group Saved"),
    });
  };

  const closeDrawer = (): void => {
    setDrawerType(GRID_ACTION_TYPES.HIDDEN);
      clearSearchAction();
  };

  const handleDelete = () =>
    deactivateMutate(data?.id ?? Constants.emptyGuid, {
      onSuccess: (response: IActionResponse) =>
        handleResponse(response, "Lookup Group Deleted"),
    });

  const renderDrawerContent = () => {
    switch (drawerType) {
      case GRID_ACTION_TYPES.NEW: {
        return (
          <EditComponent<T>
            show={true}
            onHide={closeDrawer}
            onSave={(data: T) => handleOnSubmit(data)}
            lookupCategoryLookupValues={lookupQuery?.data}
            isLoading={lookupQuery?.isLoading}
          />
        );
      }
      case GRID_ACTION_TYPES.EDIT:
        return (
          <EditComponent<T>
            data={lookupGroupQuery?.data}
            show={!!lookupGroupQuery?.data}
            onHide={closeDrawer}
            onSave={(data: T) => handleOnSubmit(data)}
            lookupCategoryLookupValues={lookupQuery?.data}
            isLoading={lookupQuery?.isLoading || lookupGroupQuery?.isLoading}
          />
        );
      case GRID_ACTION_TYPES.VIEW:
      case GRID_ACTION_TYPES.DOUBLE_CLICK:
        return (
          <LookupGroupViewComponenet<T>
            data={lookupGroupQuery?.data}
            show={!!lookupGroupQuery?.data}
            lookupCategoryLookupValues={lookupQuery?.data}
            onHide={closeDrawer}
            onDelete={handleDelete}
            onEdit={() => EditRecord(lookupGroupQuery.data)}
            isLoading={lookupQuery?.isLoading || lookupGroupQuery?.isLoading}
          />
        );
    }
  };

  function handleResponse(response: IActionResponse, snackbarMessage: string) {
    if (response) {
      if (response.isSuccess) {
        showSnackbar(snackbarMessage, MessageSeverity.Success);
        lookupGroupQuery.refetch();
        queryClient.invalidateQueries({
          queryKey: [ViewProps.LookupGroupQueryKeys[QueryKeyTypes.Grid]],
        });
        closeDrawer();
      } else {
        if (response.errorMessages) {
          const errorMsg: string | null = apiErrorMessage(
            response.errorMessages
          );
          snackbarMessage = errorMsg ?? "Error";
        }
        showSnackbar(snackbarMessage, MessageSeverity.Error);
      }
    } else {
      showSnackbar(
        "Error, unable to process your request please try again later.",
        MessageSeverity.Error
      );
    }
  }

  return (
    <>
      {lookupQuery.isLoading ? (
        <LoadingIndicator isFullPage={true} />
      ) : (
        <LookupGroupPage<T>
          onCustomEvent={OnCustomEvent}
          moduleType={props.moduleType}
          lookupCategoryLookupValues={lookupQuery?.data}
        />
      )}
      {renderDrawerContent()}
    </>
  );
};
